import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Turnstile, turnstileEnabled } from '@/components/turnstile';
import { Input, Label } from '@/components/ui/primitives';
import { env } from '@/lib/env';
import { supabase } from '@/lib/supabase';
import { AuthShell } from './login';

const schema = z.object({
  fullName: z.string().min(2, 'Tell us your name'),
  email: z.string().email('Please enter a valid email'),
  password: z
    .string()
    .min(8, 'At least 8 characters')
    .regex(/[A-Z]/, 'Include one uppercase letter')
    .regex(/[0-9]/, 'Include one number'),
  website: z.string().optional(), // honeypot — checked at runtime, not validated
});
type FormData = z.infer<typeof schema>;

export function JoinPage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState(0);
  const form = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async ({ fullName, email, password, website }: FormData) => {
    // Honeypot check — real users never fill this hidden field
    if (website) return;
    if (turnstileEnabled && !captchaToken) {
      toast.error('Please complete the quick security check.');
      return;
    }

    setSubmitting(true);
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: `${env.appUrl}/auth/callback?next=/dashboard`,
        captchaToken: captchaToken ?? undefined,
      },
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      // Turnstile tokens are single-use — get a fresh one for the retry
      setCaptchaToken(null);
      setCaptchaKey((k) => k + 1);
      return;
    }
    // Welcome email is sent from /auth/callback after the user confirms their
    // email — this prevents bot signups from triggering the devotional sequence.
    toast.success('Welcome to the sisterhood. Check your email to confirm.');
    navigate('/dashboard', { replace: true });
  };

  return (
    <AuthShell
      eyebrow="Join the sisterhood"
      title="Your next chapter starts here."
      side={
        <div className="max-w-md text-cream-50">
          <blockquote className="font-display text-3xl italic leading-tight md:text-4xl">
            "Behold, I am doing a new thing; now it springs forth, do you not perceive it?"
          </blockquote>
          <div className="mt-4 text-sm uppercase tracking-[0.3em] text-cream-200/70">
            Isaiah 43:19
          </div>
        </div>
      }
    >
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
        {/* Honeypot — hidden from real users, bots fill it in */}
        <input
          {...form.register('website')}
          type="text"
          tabIndex={-1}
          aria-hidden="true"
          autoComplete="off"
          style={{ display: 'none' }}
        />
        <div className="space-y-2">
          <Label htmlFor="fullName">Your name</Label>
          <Input id="fullName" autoComplete="name" {...form.register('fullName')} />
          {form.formState.errors.fullName && (
            <p className="text-xs text-destructive">{form.formState.errors.fullName.message}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoComplete="email" {...form.register('email')} />
          {form.formState.errors.email && (
            <p className="text-xs text-destructive">{form.formState.errors.email.message}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            {...form.register('password')}
          />
          {form.formState.errors.password ? (
            <p className="text-xs text-destructive">{form.formState.errors.password.message}</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              At least 8 characters, with one uppercase letter and one number.
            </p>
          )}
        </div>
        <Turnstile key={captchaKey} onToken={setCaptchaToken} theme="light" />
        <Button type="submit" size="lg" className="w-full" disabled={submitting}>
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {submitting ? 'Creating your space...' : 'Create my account'}
        </Button>
      </form>

      <div className="mt-8 text-center text-sm text-muted-foreground">
        Already with us?{' '}
        <Link to="/login" className="font-medium text-primary hover:underline">
          Sign in
        </Link>
      </div>
    </AuthShell>
  );
}
